import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { ReportIndicators } from "@planzone/dto";
import { ReportIndicator } from "@common/reportIndicator";
import { HorizontalList } from "@common/horizontalList";
import { DragAndDrop, Draggable, DropBox } from "@common/dragAndDrop";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { AddIcon } from "../indicatorsForm/addIcon";
import { RemoveIcon } from "../indicatorsForm/removeIcon";
import { MoveIcon } from "../textFields/moveIcon";
import { Props } from "./types";
import styles from "./styles.scss";

function ReportPreview({
  indicators,
  textFields,
  criteriaTemplate,
  isDisabled,
  editIndicators,
  submitIndicators,
  editFields,
  submitFields,
}: Props) {
  const { t } = useTranslation();
  const [pendingFocusOrder, setPendingFocusOrder] = React.useState<
    number | null
  >(null);
  const sortedFields = [...textFields].sort((a, b) =>
    a.order < b.order ? -1 : 1,
  );
  const firstFieldIndex = textFields.reduce(
    (order, field) => (order > field.order ? field.order : order),
    0,
  );
  const lastFieldIndex = textFields.reduce(
    (order, field) => (order < field.order ? field.order : order),
    0,
  );

  return (
    <div className={styles.preview}>
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>{t("projectReport.title")}</h2>
      </div>
      <div className={styles.body}>
        <input
          className={styles.nameInput}
          placeholder={t("projectReportSubmission.namePlaceholder")}
          readOnly
          disabled
        />
        <p className={styles.mockInfo}>
          {t("projectReport.submissionDate")} --/--/---- {t("projectReport.by")}{" "}
          ------
        </p>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t("utilityPage.projectReportTemplatePage.indicators")}
          </h3>
          <DragAndDrop>
            <HorizontalList>
              <div className={styles.indicatorsList}>
                {new Array(5).fill(0).map((_, index) => (
                  <DropBox
                    key={index}
                    acceptedTypes={["moveIndicator"]}
                    drop={(data) => {
                      const sourceOrder = data.payload.order;
                      submitIndicators(
                        indicators.map((f) => {
                          if (f.order === sourceOrder)
                            return { ...f, order: index };
                          if (f.order === index)
                            return { ...f, order: sourceOrder };
                          return f;
                        }),
                      );
                    }}
                  >
                    {(data, canDrop) => {
                      const indicator = canDrop
                        ? data.payload
                        : indicators.find(({ order }) => order === index);

                      return indicator ? (
                        <Draggable
                          type="moveIndicator"
                          data={indicator}
                          className={styles.indicatorSlot}
                        >
                          <div className={styles.indicatorVisual}>
                            <ReportIndicator
                              className={styles.indicatorIcon}
                              value={ReportIndicators.VERY_GOOD}
                            />
                            <div
                              className={styles.indicatorRemove}
                              onClick={() => {
                                const next = indicators.filter(
                                  (i) => i.order !== index,
                                );
                                editIndicators(next);
                                submitIndicators(next);
                              }}
                            >
                              <RemoveIcon className={styles.removeSmallIcon} />
                            </div>
                          </div>
                          <input
                            ref={(el) => {
                              if (el && pendingFocusOrder === index) {
                                el.focus();
                                setPendingFocusOrder(null);
                              }
                            }}
                            className={styles.indicatorNameInput}
                            placeholder={t(
                              "utilityPage.projectReportTemplatePage.indicatorsForm.placeholder",
                            )}
                            disabled={isDisabled}
                            value={indicator.name}
                            onKeyUp={({ key }) =>
                              key === "Enter" && submitIndicators(indicators)
                            }
                            onBlur={() => submitIndicators(indicators)}
                            onChange={({ target }) =>
                              editIndicators(
                                indicators.map((i) =>
                                  i.order === index
                                    ? { ...indicator, name: target.value }
                                    : i,
                                ),
                              )
                            }
                          />
                        </Draggable>
                      ) : (
                        <div
                          className={classNames(
                            styles.indicatorSlot,
                            styles.addIndicatorSlot,
                          )}
                          onClick={() => {
                            editIndicators([
                              ...indicators,
                              { type: "indicator", name: "", order: index },
                            ]);
                            setPendingFocusOrder(index);
                          }}
                        >
                          <AddIcon className={styles.indicatorIcon} />
                          <input
                            className={styles.indicatorNameInput}
                            placeholder=""
                            value=""
                            readOnly
                          />
                        </div>
                      );
                    }}
                  </DropBox>
                ))}
              </div>
            </HorizontalList>
          </DragAndDrop>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t("utilityPage.projectReportTemplatePage.fields")}
          </h3>
          <DragAndDrop>
            <div className={styles.fieldsList}>
              {sortedFields.map((field, index) => (
                <React.Fragment key={field.order}>
                  <div
                    className={classNames(styles.fieldCard, {
                      [styles.fieldDisabled]: isDisabled,
                    })}
                  >
                    <div className={styles.fieldHeader}>
                      <Draggable data={field} type="moveField">
                        <div className={styles.moveHandle}>
                          <MoveIcon className={styles.moveIcon} />
                        </div>
                      </Draggable>
                      <input
                        className={styles.fieldNameInput}
                        placeholder={t(
                          "utilityPage.projectReportTemplatePage.textFieldsForm.placeholder",
                        )}
                        value={field.name}
                        onChange={({ target }) =>
                          editFields(
                            sortedFields.map((f, i) =>
                              i === index
                                ? { ...field, name: target.value }
                                : f,
                            ),
                          )
                        }
                        disabled={isDisabled}
                        onKeyUp={({ key }) =>
                          key === "Enter" && submitFields(textFields)
                        }
                        onBlur={() => submitFields(textFields)}
                      />
                      <div
                        className={styles.fieldRemove}
                        onClick={() => {
                          if (isDisabled) return;
                          const next = sortedFields
                            .filter((_, i) => i !== index)
                            .map((f, i) => ({ ...f, order: i }));
                          editFields(next);
                          submitFields(next);
                        }}
                      >
                        &times;
                      </div>
                    </div>
                    {field.criteriaId &&
                    criteriaTemplate.find(
                      (c) => c.uuid === field.criteriaId,
                    ) ? (
                      <div className={styles.fieldBody}>
                        <SecondaryCriteriaForm
                          criteria={
                            criteriaTemplate.find(
                              (c) => c.uuid === field.criteriaId,
                            )!
                          }
                          value=""
                          disabled
                          submit={() => {}}
                        />
                      </div>
                    ) : (
                      <div className={styles.fieldTextarea} />
                    )}
                  </div>
                  <DropBox
                    acceptedTypes={["moveField", "addCriteria"]}
                    isDisabled={(data) =>
                      (data.type === "moveField" &&
                        data.payload.order === field.order) ||
                      (data.type === "addCriteria" &&
                        sortedFields.some(
                          (f) => f.criteriaId === data.payload.uuid,
                        ))
                    }
                    verticalSlop={20}
                    horizontalSlop={40}
                    drop={(data) => {
                      if (data.type === "addCriteria") {
                        if (
                          sortedFields.some(
                            (f) => f.criteriaId === data.payload.uuid,
                          )
                        )
                          return;
                        submitFields(
                          [
                            ...sortedFields.slice(0, index + 1),
                            {
                              name: data.payload.name,
                              type: "text" as const,
                              order: index + 1,
                              criteriaId: data.payload.uuid,
                            },
                            ...sortedFields.slice(index + 1),
                          ].map((f, i) => ({ ...f, order: i })),
                        );
                      } else {
                        submitFields(
                          [
                            ...sortedFields.slice(0, index + 1),
                            { ...data.payload, order: index + 1 },
                            ...sortedFields.slice(index + 1),
                          ]
                            .filter((f) => f.order !== data.payload.order)
                            .map((f, i) => ({ ...f, order: i })),
                        );
                      }
                    }}
                  >
                    {(data, canDrop) =>
                      canDrop && (
                        <div className={styles.dropPreview}>
                          <div className={styles.dropPreviewHeader}>
                            {data.payload.name}
                          </div>
                          <div className={styles.dropPreviewBody} />
                        </div>
                      )
                    }
                  </DropBox>
                </React.Fragment>
              ))}
              <DropBox
                acceptedTypes={["moveField", "addCriteria"]}
                verticalSlop={20}
                isDisabled={(data) =>
                  data.type === "addCriteria" &&
                  sortedFields.some((f) => f.criteriaId === data.payload.uuid)
                }
                drop={(data) => {
                  if (data.type === "addCriteria") {
                    if (
                      sortedFields.some(
                        (f) => f.criteriaId === data.payload.uuid,
                      )
                    )
                      return;
                    submitFields([
                      ...sortedFields.map((f, i) => ({ ...f, order: i })),
                      {
                        name: data.payload.name,
                        type: "text",
                        order: sortedFields.length,
                        criteriaId: data.payload.uuid,
                      },
                    ]);
                  } else {
                    submitFields([
                      ...sortedFields
                        .filter((f) => f.order !== data.payload.order)
                        .map((f, i) => ({ ...f, order: i })),
                      {
                        ...data.payload,
                        order: sortedFields.length - 1,
                      },
                    ]);
                  }
                }}
              >
                {(data, canDrop) =>
                  canDrop ? (
                    <div className={styles.dropPreview}>
                      <div className={styles.dropPreviewHeader}>
                        {data.payload.name}
                      </div>
                      <div className={styles.dropPreviewBody} />
                    </div>
                  ) : (
                    <div className={styles.emptyDropzone}>
                      <p className={styles.emptyDropzoneText}>
                        {t(
                          "utilityPage.projectReportTemplatePage.emptyFieldsDropzone",
                        )}
                      </p>
                    </div>
                  )
                }
              </DropBox>
            </div>
          </DragAndDrop>
        </div>
      </div>
    </div>
  );
}

export { ReportPreview };
